import { useState, useEffect, useRef, useMemo } from "react";

const ME_ANIMATIONS = [
  "/meAnimation/1.png", 
  "/meAnimation/3.png", 
  "/meAnimation/4.png", 
  "/meAnimation/5.png"
];
const IDLE_ANIMATION = [
  "/meAnimation/6.png", 
  "/meAnimation/7.png", 
  "/meAnimation/8.png"
];
const STANDING_IMAGE = "/meAnimation/2.png";

const FALLBACK_IMAGE = ME_ANIMATIONS[0]; 
const ALL_FRAMES = [...ME_ANIMATIONS, ...IDLE_ANIMATION, STANDING_IMAGE];

function Animation({ scrolled }) {
  const [ready, setReady] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [meImageIndex, setMeImageIndex] = useState(0);
  const [idleIndex, setIdleIndex] = useState(0);
  const [status, setStatus] = useState("walking");

  const animationTimeout = useRef(null);
  const standingTimeout = useRef(null);

  // Preload logic: Keep images in browser cache
  useEffect(() => {
    let mounted = true;
    const preload = async () => {
      const promises = ALL_FRAMES.map(src => {
        return new Promise(resolve => {
          const img = new Image();
          img.src = src;
          img.onload = resolve;
          img.onerror = resolve;
        });
      });
      await Promise.all(promises);
      if (mounted) setReady(true);
    };
    preload();
    return () => { mounted = false; };
  }, []);

  // Idle Timer logic
  useEffect(() => {
    if (status !== "idle") return;
    const interval = setInterval(() => {
      setIdleIndex((i) => (i + 1) % IDLE_ANIMATION.length);
    }, 500);
    return () => clearInterval(interval);
  }, [status]);

  // Optimized Scroll Handler
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      const nextIndex = Math.floor(scrollY / 150) % ME_ANIMATIONS.length;

      setMeImageIndex(prev => (prev !== nextIndex ? nextIndex : prev));
      setStatus("walking");

      if (animationTimeout.current) clearTimeout(animationTimeout.current);
      if (standingTimeout.current) clearTimeout(standingTimeout.current);

      animationTimeout.current = setTimeout(() => {
        setStatus("standing");
        standingTimeout.current = setTimeout(() => {
          setStatus("idle");
        }, 1200);
      }, 120);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(animationTimeout.current);
      clearTimeout(standingTimeout.current);
    };
  }, []);

  const currentImage = useMemo(() => {
    if (!ready) return FALLBACK_IMAGE;
    if (status === "walking") return ME_ANIMATIONS[meImageIndex];
    if (status === "standing") return STANDING_IMAGE;
    return IDLE_ANIMATION[idleIndex];
  }, [ready, status, meImageIndex, idleIndex]);

  return (
    <div className={`walking-page ${scrolled ? "walking-small" : ""}`}>
      <img
        className={`walking-animation ${imageLoaded ? "is-visible" : "is-hidden"}`}
        src={currentImage}
        alt="" 
        onLoad={() => setImageLoaded(true)}
        draggable={false}
        style={{
          transition: "opacity 0.1s ease-in-out",
          opacity: imageLoaded ? 1 : 0
        }}
      />
    </div>
  );
}

export default Animation;